import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useApp } from '../App';
import { PLANS } from '../data/constants';
import { post } from '../data/api';
import LocationDropdown from './LocationDropdown';

const PROPERTY_TYPES = [
  { id: 'farm',       label: 'Farm / Land', icon: '🌾' },
  { id: 'house',      label: 'House',       icon: '🏠' },
  { id: 'apartment',  label: 'Apartment',   icon: '🏢' },
  { id: 'commercial', label: 'Commercial',  icon: '🏪' },
  { id: 'plot',       label: 'Plot',        icon: '📍' },
  { id: 'other',      label: 'Other',       icon: '📦' },
];

const STEPS = ['Your Account', 'Choose Plan', 'Add Property', 'Add Caretaker'];

export default function Signup() {
  const navigate      = useNavigate();
  const routerLoc     = useLocation();
  const { login }     = useApp();

  const initialPlan = routerLoc.state?.planId || 'pro';
  const [step, setStep] = useState(1);

  // Step 1 — account
  const [fullName,  setFullName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [country,   setCountry]   = useState('');

  // Step 2 — plan
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);

  // Step 3 — property
  const [propName,     setPropName]     = useState('');
  const [propType,     setPropType]     = useState('farm');
  const [kenLocation,  setKenLocation]  = useState({ county: '', sub_county: '', ward: '' });

  // Step 4 — caretaker
  const [ctName,  setCtName]  = useState('');
  const [ctPhone, setCtPhone] = useState('');

  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  function validateStep1() {
    const e = {};
    if (!fullName.trim())  e.fullName  = 'Full name is required';
    if (!email.trim())     e.email     = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Invalid email address';
    if (!password)         e.password  = 'Password is required';
    else if (password.length < 6) e.password = 'Password must be at least 6 characters';
    return e;
  }

  function validateStep3() {
    const e = {};
    if (!propName.trim())   e.propName     = 'Property name is required';
    if (!kenLocation.county)   e.propLocation = 'Please select a county';
    return e;
  }

  function handleNext() {
    setApiError('');
    if (step === 1) {
      const e = validateStep1();
      if (Object.keys(e).length) { setErrors(e); return; }
    }
    if (step === 3) {
      const e = validateStep3();
      if (Object.keys(e).length) { setErrors(e); return; }
    }
    setErrors({});
    setStep(s => s + 1);
  }

  function handleBack() { setErrors({}); setStep(s => s - 1); }

  async function doRegister(withCaretaker) {
    setLoading(true);
    setApiError('');
    const plan = PLANS.find(p => p.id === selectedPlan);
    try {
      const data = await post('register', {
        full_name:  fullName.trim(),
        email:      email.trim().toLowerCase(),
        password,
        plan:       selectedPlan,
        credits:    plan.credits,
        country:    country.trim(),
      });
      if (!data.success) { setApiError(data.error || 'Registration failed.'); return; }

      // Attach property + caretaker to the response user object for context
      const locLabel = [kenLocation.ward, kenLocation.sub_county, kenLocation.county]
        .filter(Boolean).join(', ');
      data.properties = [{
        id:              1,
        name:            propName,
        type:            propType,
        location:        locLabel,
        county:          kenLocation.county,
        sub_county:      kenLocation.sub_county,
        ward:            kenLocation.ward,
        caretaker_name:  withCaretaker ? ctName  : null,
        caretaker_phone: withCaretaker ? ctPhone : null,
      }];

      login(data);
      navigate('/dashboard', { replace: true });
    } catch {
      setApiError('Could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleFinish()        { doRegister(true); }
  function handleSkipCaretaker() { doRegister(false); }

  return (
    <div className="signup-page">
      <header className="signup-header">
        <div className="signup-logo">mudiaspora.</div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')}>
          ← Back to home
        </button>
      </header>

      <div className="signup-body">
        {/* Stepper */}
        <div className="stepper">
          {STEPS.map((label, i) => {
            const n      = i + 1;
            const active = n === step;
            const done   = n < step;
            return (
              <React.Fragment key={label}>
                {i > 0 && <div className={`stepper-line ${done || active ? 'done' : ''}`} />}
                <div className={`stepper-step ${active ? 'active' : ''} ${done ? 'done' : ''}`}>
                  <div className="stepper-dot">{done ? '✓' : n}</div>
                  <span className="stepper-label">{label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {apiError && (
          <div className="alert alert-error" style={{ marginBottom: 16, maxWidth: 560, margin: '0 auto 16px' }}>
            {apiError}
          </div>
        )}

        {/* ── STEP 1: Account ── */}
        {step === 1 && (
          <div className="signup-card">
            <h2 className="signup-card-title">Create your account</h2>
            <p className="signup-card-sub">Free to start — no credit card required.</p>

            <div className="form-group">
              <label htmlFor="fullName">Full name</label>
              <input
                id="fullName"
                type="text"
                className="form-control"
                placeholder="e.g. Grace Wanjiru"
                value={fullName}
                onChange={e => { setFullName(e.target.value); setErrors({}); }}
                autoFocus
              />
              {errors.fullName && <span className="form-hint text-red">{errors.fullName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                className="form-control"
                placeholder="you@example.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setErrors({}); }}
              />
              {errors.email && <span className="form-hint text-red">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                className="form-control"
                placeholder="At least 6 characters"
                value={password}
                onChange={e => { setPassword(e.target.value); setErrors({}); }}
              />
              {errors.password && <span className="form-hint text-red">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="country">Country you live in</label>
              <input
                id="country"
                type="text"
                className="form-control"
                placeholder="e.g. United Kingdom"
                value={country}
                onChange={e => setCountry(e.target.value)}
              />
            </div>

            <div className="signup-nav">
              <p className="text-muted text-sm">
                Already have an account?{' '}
                <Link to="/login" style={{ color: 'var(--green)', fontWeight: 600 }}>Sign in</Link>
              </p>
              <button className="btn btn-primary btn-lg" onClick={handleNext}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Plan ── */}
        {step === 2 && (
          <div className="signup-card">
            <h2 className="signup-card-title">Choose your plan</h2>
            <p className="signup-card-sub">
              Credits are included each month. Top up any time — they never expire.
            </p>
            <div className="plan-select-grid">
              {PLANS.map(p => (
                <div
                  key={p.id}
                  className={`plan-select-card ${selectedPlan === p.id ? 'selected' : ''} ${p.featured ? 'featured' : ''}`}
                  onClick={() => setSelectedPlan(p.id)}
                >
                  {p.featured && (
                    <div className="plan-select-featured-badge">Most Popular</div>
                  )}
                  <div className="plan-select-radio">
                    <div className="plan-select-radio-dot" />
                  </div>
                  <div className="plan-select-name">{p.name}</div>
                  <div className="plan-select-price">
                    <sup>$</sup>{p.price}
                    <span className="text-muted text-sm" style={{ fontSize: 14 }}>/mo</span>
                  </div>
                  <div className="plan-select-credits">🪙 {p.credits} credits / mo</div>
                  <ul className="plan-select-features">
                    {p.features.map(f => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            <div className="signup-nav">
              <button className="btn btn-ghost" onClick={handleBack}>← Back</button>
              <button className="btn btn-primary btn-lg" onClick={handleNext}>
                Continue with {PLANS.find(p => p.id === selectedPlan)?.name} →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Property ── */}
        {step === 3 && (
          <div className="signup-card">
            <h2 className="signup-card-title">Add your first property</h2>
            <p className="signup-card-sub">
              Tell us what you need managed. You can add more properties later.
            </p>

            <div style={{ marginBottom: 20 }}>
              <div className="form-group mb-0">
                <label>Property type</label>
              </div>
              <div className="property-types">
                {PROPERTY_TYPES.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className={`property-type-btn ${propType === t.id ? 'active' : ''}`}
                    onClick={() => setPropType(t.id)}
                  >
                    <span className="property-type-icon">{t.icon}</span>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="propName">Property name / nickname</label>
              <input
                id="propName"
                type="text"
                className="form-control"
                placeholder="e.g. Eldoret Farm, Mum's House"
                value={propName}
                onChange={e => { setPropName(e.target.value); setErrors({}); }}
              />
              {errors.propName && <span className="form-hint text-red">{errors.propName}</span>}
            </div>

            <div className="form-group">
              <label>Location in Kenya</label>
              <LocationDropdown onChange={setKenLocation} values={kenLocation} />
              {errors.propLocation && <span className="form-hint text-red">{errors.propLocation}</span>}
            </div>

            <div className="signup-nav">
              <button className="btn btn-ghost" onClick={handleBack}>← Back</button>
              <button className="btn btn-primary btn-lg" onClick={handleNext}>Continue →</button>
            </div>
          </div>
        )}

        {/* ── STEP 4: Caretaker ── */}
        {step === 4 && (
          <div className="signup-card">
            <h2 className="signup-card-title">Add a caretaker (optional)</h2>
            <p className="signup-card-sub">
              If someone already looks after your property, add them here. Your agent
              will coordinate with them directly.
            </p>

            <div className="signup-skip-row">
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleSkipCaretaker}
                disabled={loading}
              >
                Skip for now →
              </button>
            </div>

            <div className="form-group">
              <label htmlFor="ctName">Caretaker's full name</label>
              <input
                id="ctName"
                type="text"
                className="form-control"
                placeholder="e.g. Joseph Kibet"
                value={ctName}
                onChange={e => setCtName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ctPhone">M-Pesa phone number</label>
              <input
                id="ctPhone"
                type="tel"
                className="form-control"
                placeholder="e.g. 0712 345 678"
                value={ctPhone}
                onChange={e => setCtPhone(e.target.value)}
              />
              <span className="form-hint">Used by your agent to coordinate and send reports</span>
            </div>

            <div className="signup-nav">
              <button className="btn btn-ghost" onClick={handleBack} disabled={loading}>← Back</button>
              <button
                className="btn btn-primary btn-lg"
                onClick={handleFinish}
                disabled={loading}
              >
                {loading ? 'Creating account…' : 'Go to Dashboard →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
